/**
 * VerdictCard — the focus pane of the Verdict Queue (UI-SPEC §4): a single run
 * awaiting a human verdict. Shows the workflow, the agent output, the judge score,
 * and why it surfaced. Accept/Reject/Edit actions are injected by the page
 * (client island). Presentational.
 */

import * as React from "react";
import { Card, CardContent } from "../../card";
import { StatusBadge } from "../status-badge";
import { renderDomainOutput } from "./domain-renderers";

export interface VerdictRunFocus {
  id: number;
  loop_slug: string | null;
  role: string;
  model: string | null;
  outcome: string | null;
  judge_score: number | null;
  started_at: string;
  output_excerpt: string | null;
  why: string;
}

export function VerdictCard({
  run,
  actions,
  domain,
}: {
  run: VerdictRunFocus;
  actions?: React.ReactNode;
  /** Workflow domain → renders the output legibly (UI-SPEC §8 domain renderers). */
  domain?: string | null;
}) {
  return (
    <Card className="py-0">
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="type-body font-medium text-foreground">
            {run.loop_slug ?? "workflow"} · run #{run.id}
          </span>
          {run.outcome && <StatusBadge domain="worker_run" status={run.outcome === "pass" ? "completed" : "failed"} label={run.outcome} />}
          <span className="font-mono type-subtext text-muted-foreground">{run.model ?? "—"}</span>
        </div>
        <p className="type-subtext text-muted-foreground">why surfaced: {run.why}</p>
        <div className="flex flex-col gap-1">
          <span className="type-subtext text-muted-foreground">agent output</span>
          {renderDomainOutput(domain, run.output_excerpt ?? "(no output captured)")}
        </div>
        <div className="type-subtext text-muted-foreground">
          judge score: {run.judge_score === null ? "—" : `${(run.judge_score * 100).toFixed(0)}%`}
        </div>
        {actions && <div className="flex flex-wrap gap-2 pt-1">{actions}</div>}
      </CardContent>
    </Card>
  );
}
